/**
 * @file App.js
 * @description Componente principal (Controller).
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { LogIn, LogOut, Shield, Star, Trophy } from "lucide-react";

// Importações da nossa estrutura MVC
import * as api from "./api/ligaApi";
import LoginPage from "./views/LoginPage";
import AdminPanel from "./views/AdminPanel";
import Dashboard from "./views/Dashboard";
import CopaLoader from "./components/CopaLoader";
import { useModal } from "./contexts/ModalContext";

// Bola de futebol estilizada (gomos) para o fundo decorativo
const BolaSvg = (props) => (
  <svg viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="1.5" {...props}>
    <circle cx="50" cy="50" r="48" />
    <polygon points="50,32 67,44 60.5,64.5 39.5,64.5 33,44" fill="currentColor" stroke="none" />
    <path d="M50 32 L50 2 M67 44 L95.5 35 M60.5 64.5 L78 89 M39.5 64.5 L22 89 M33 44 L4.5 35" />
  </svg>
);

// Formas da Copa no fundo do site (opacidade baixa, atrás do conteúdo)
const BackgroundCopa = () => (
  <div aria-hidden="true" className="fixed inset-0 overflow-hidden pointer-events-none">
    {/* Brilho dourado no topo */}
    <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[40rem] h-80 bg-ouro/[0.07] rounded-full blur-3xl" />
    {/* Bola de futebol */}
    <BolaSvg className="absolute -right-20 top-32 w-64 h-64 sm:w-80 sm:h-80 text-tinta/[0.05] rotate-12" />
    {/* Troféu */}
    <Trophy
      strokeWidth={1}
      className="absolute -left-12 bottom-8 w-52 h-52 sm:w-72 sm:h-72 text-ouro/[0.08] -rotate-12"
    />
    {/* Estrelas espalhadas */}
    <Star className="absolute left-[12%] top-[22%] w-5 h-5 text-ouro/15 fill-ouro/15 rotate-12" />
    <Star className="absolute right-[18%] top-[58%] w-4 h-4 text-ouro/15 fill-ouro/15 -rotate-6" />
    <Star className="absolute left-[28%] bottom-[14%] w-6 h-6 text-ouro/[0.12] fill-ouro/[0.12] rotate-45" />
    <Star className="absolute right-[35%] top-[12%] w-3.5 h-3.5 text-tinta/[0.06] fill-tinta/[0.06]" />
  </div>
);

export default function App() {
  const [isAdmin, setIsAdmin] = useState(false);
  const [view, setView] = useState("DASHBOARD");
  const [loading, setLoading] = useState(true);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false); // NOVO ESTADO
  const [pdfStage, setPdfStage] = useState("");
  const [error, setError] = useState("");
  const [ligaData, setLigaData] = useState({ equipes: [], rodadas: [] });
  const [parciais, setParciais] = useState(null);
  const [admins, setAdmins] = useState([]);
  // Depois do primeiro load os refreshes são silenciosos (sem loader global),
  // para a tela não piscar a cada ação do admin
  const hasLoadedRef = useRef(false);
  const modal = useModal();

  const handleLogout = useCallback(() => {
    localStorage.removeItem("adminToken");
    setIsAdmin(false);
    setAdmins([]);
    setView("DASHBOARD");
  }, []);

  const fetchData = useCallback(async () => {
    try {
      if (!hasLoadedRef.current) setLoading(true);
      setError("");
      const dadosLiga = await api.getDadosCompletos();
      setLigaData(dadosLiga);
      hasLoadedRef.current = true;

      if (localStorage.getItem("adminToken")) {
        const dadosAdmins = await api.getAdmins();
        setAdmins(dadosAdmins);
      }
    } catch (err) {
      if (err.message.includes("Sessão inválida")) {
        handleLogout();
      } else if (!hasLoadedRef.current) {
        // Com dados na tela, uma falha de refresh não derruba a página
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [handleLogout]);

  useEffect(() => {
    if (localStorage.getItem("adminToken")) {
      setIsAdmin(true);
    }
    fetchData();
  }, [fetchData]);

  // Parciais ao vivo: o backend cacheia as respostas do Cartola, então o
  // polling de 60s aqui não multiplica requisições externas
  useEffect(() => {
    let ativo = true;
    const buscarParciais = () =>
      api
        .getParciais()
        .then((dados) => {
          if (!ativo) return;
          // Só re-renderiza se a parcial realmente mudou
          setParciais((prev) =>
            prev && JSON.stringify(prev) === JSON.stringify(dados)
              ? prev
              : dados
          );
        })
        .catch(() => {});
    buscarParciais();
    const intervalo = setInterval(buscarParciais, 60 * 1000);
    return () => {
      ativo = false;
      clearInterval(intervalo);
    };
  }, []);

  // --- LÓGICA DE GERAÇÃO DE PDF ---
  const generatePdf = async (data, subTitle, filename) => {
    setIsGeneratingPdf(true); // ATIVA A ANIMAÇÃO
    setPdfStage("Carregando imagens...");
    try {
        // jsPDF só é baixado quando alguém gera um PDF (fora do bundle inicial)
        const { jsPDF } = await import("jspdf");
        const CARD_MARGIN = 15;
        const CARD_SPACING = 7;
        const HEADER_HEIGHT = 16;
        const ROW_HEIGHT = 14;
        const FOOTER_HEIGHT = 16;

        // Converte cada imagem para um dataURL reduzido via canvas. Passar a
        // URL direto ao jsPDF dispara XHR síncrono por imagem (trava a tela,
        // principalmente no mobile) e embute o arquivo em tamanho cheio.
        const loadImage = (url) => {
          return new Promise((resolve) => {
            if (!url) return resolve(null);
            // imagem que não responder em 8s sai do relatório em vez de travar
            const timeout = setTimeout(() => resolve(null), 8000);
            const finalizar = (valor) => {
              clearTimeout(timeout);
              resolve(valor);
            };
            const img = new Image();
            img.crossOrigin = "Anonymous";
            img.onload = () => {
              try {
                const MAX = 96; // os escudos saem com ~10mm no PDF
                const ratio = Math.min(1, MAX / Math.max(img.width, img.height));
                const canvas = document.createElement("canvas");
                canvas.width = Math.max(1, Math.round(img.width * ratio));
                canvas.height = Math.max(1, Math.round(img.height * ratio));
                canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
                finalizar({
                  src: canvas.toDataURL("image/png"),
                  width: canvas.width,
                  height: canvas.height,
                });
              } catch {
                finalizar(null);
              }
            };
            img.onerror = () => finalizar(null);
            img.src = url;
          });
        };

        const allTimes = data.flatMap((equipe) => equipe.times);
        const escudoPromises = allTimes.map((time) =>
          loadImage(api.getEscudoUrl(time.escudo_url))
        );
        const logoPromises = data.map((equipe) => loadImage(api.getEscudoUrl(equipe.logo_url)));

        const loadedEscudos = await Promise.all(escudoPromises);
        const loadedLogos = await Promise.all(logoPromises);
        setPdfStage("Montando o PDF...");
        
        const escudoMap = allTimes.reduce((map, time, index) => {
          if (loadedEscudos[index]) map[time.id] = loadedEscudos[index];
          return map;
        }, {});
        const logoMap = data.reduce((map, equipe, index) => {
            if(loadedLogos[index]) map[equipe.id] = loadedLogos[index];
            return map;
        }, {});

        let totalHeight = 40;
        data.forEach((equipe) => {
          totalHeight +=
            HEADER_HEIGHT +
            equipe.times.length * ROW_HEIGHT +
            FOOTER_HEIGHT +
            CARD_SPACING;
        });
        totalHeight += 20;

        const pageWidth = 210;
        const doc = new jsPDF({
          orientation: "p",
          unit: "mm",
          format: [pageWidth, totalHeight],
        });

        let yPos = 0;

        doc.setFillColor("#F6F4F2");
        doc.rect(0, 0, pageWidth, totalHeight, "F");

        // Faixa de destaque no topo
        doc.setFillColor("#EF6106");
        doc.rect(0, 0, pageWidth, 2, "F");

        doc.setFont("helvetica", "bold");
        doc.setFontSize(22);
        doc.setTextColor("#141112");
        doc.text("TORNEIO CFC PARAÍBA DE EQUIPES", pageWidth / 2, 22, { align: "center" });

        doc.setFont("helvetica", "normal");
        doc.setFontSize(12);
        doc.setTextColor("#EF6106");
        doc.text(subTitle, pageWidth / 2, 30, { align: "center" });

        yPos = 42;

        for (const [index, equipe] of data.entries()) {
          const cardHeight =
            HEADER_HEIGHT + equipe.times.length * ROW_HEIGHT + FOOTER_HEIGHT;

          doc.setFillColor("#FFFFFF");
          doc.roundedRect(
            CARD_MARGIN,
            yPos,
            pageWidth - CARD_MARGIN * 2,
            cardHeight,
            3,
            3,
            "F"
          );

          const headerYCenter = yPos + HEADER_HEIGHT / 2;
          doc.setFillColor("#FFF2E9");
          doc.roundedRect(
            CARD_MARGIN,
            yPos,
            pageWidth - CARD_MARGIN * 2,
            HEADER_HEIGHT,
            3,
            3,
            "F"
          );
          
          const logo = logoMap[equipe.id];
          const hasLogo = !!logo;

          if (hasLogo) {
              const maxDim = 10;
              const ratio = Math.min(maxDim / logo.width, maxDim / logo.height);
              const imgWidth = logo.width * ratio;
              const imgHeight = logo.height * ratio;
              const yOffset = headerYCenter - (imgHeight / 2);
              doc.addImage(logo.src, 'PNG', CARD_MARGIN + 4, yOffset, imgWidth, imgHeight, undefined, 'FAST');
          }

          doc.setFont("helvetica", "bold");
          doc.setFontSize(16);
          doc.setTextColor("#68615E");
          doc.text(`${index + 1}º`, CARD_MARGIN + (hasLogo ? 16 : 5), headerYCenter, {
            baseline: "middle",
          });

          doc.setFont("helvetica", "bold");
          doc.setFontSize(14);
          doc.setTextColor("#141112");
          doc.text(equipe.nome, CARD_MARGIN + (hasLogo ? 29 : 18), headerYCenter, {
            baseline: "middle",
          });

          let rowY = yPos + HEADER_HEIGHT;

          // ORDENAÇÃO CORRIGIDA: PONTUAÇÃO E DEPOIS ALFABÉTICA
          const timesOrdenados = equipe.times.sort((a, b) => {
            if (b.pontuacao !== a.pontuacao) {
              return b.pontuacao - a.pontuacao;
            }
            return a.nome_time.localeCompare(b.nome_time);
          });

          for (const time of timesOrdenados) {
            doc.setDrawColor("#E8E2DE");
            doc.setLineWidth(0.2);
            doc.line(CARD_MARGIN + 2, rowY, pageWidth - CARD_MARGIN - 2, rowY);

            rowY += ROW_HEIGHT;

            const escudo = escudoMap[time.id];
            if (escudo) {
              const maxDim = 10;
              const ratio = Math.min(maxDim / escudo.width, maxDim / escudo.height);
              const imgWidth = escudo.width * ratio;
              const imgHeight = escudo.height * ratio;
              doc.addImage(
                escudo.src,
                "PNG",
                CARD_MARGIN + 4,
                rowY - ROW_HEIGHT / 2 - (imgHeight/2),
                imgWidth,
                imgHeight,
                undefined,
                'FAST'
              );
            }

            const textX = CARD_MARGIN + 17;
            doc.setFont("helvetica", "bold");
            doc.setFontSize(9);
            doc.setTextColor("#141112");
            doc.text(time.nome_time, textX, rowY - 8);
            
            if(time.is_captain) {
                doc.setFont("helvetica", "bold");
                doc.setFontSize(9);
                doc.setTextColor("#EF6106");
                const timeNameWidth = doc.getTextWidth(time.nome_time);
                doc.text("(C)", textX + timeNameWidth + 2, rowY - 8);
            }

            doc.setFont("helvetica", "normal");
            doc.setFontSize(8);
            doc.setTextColor("#68615E");
            doc.text(time.nome_cartoleiro, textX, rowY - 3.5);

            doc.setFont("helvetica", "bold");
            doc.setFontSize(11);
            doc.setTextColor("#141112");
            doc.text(
              time.pontuacao.toFixed(2),
              pageWidth - CARD_MARGIN - 4,
              rowY - 7,
              { align: "right" }
            );
          }

          const footerY = yPos + cardHeight - FOOTER_HEIGHT;
          doc.setFillColor("#FFF2E9");
          doc.roundedRect(
            CARD_MARGIN,
            footerY,
            pageWidth - CARD_MARGIN * 2,
            FOOTER_HEIGHT,
            3,
            3,
            "F"
          );

          doc.setFont("helvetica", "normal");
          doc.setFontSize(9);
          doc.setTextColor("#68615E");
          doc.text("PONTUAÇÃO TOTAL DA EQUIPE", pageWidth / 2, footerY + 7, {
            align: "center",
          });

          doc.setFont("helvetica", "bold");
          doc.setFontSize(16);
          doc.setTextColor("#EF6106");
          doc.text(equipe.pontuacao.toFixed(2), pageWidth / 2, footerY + 13.5, {
            align: "center",
          });

          yPos += cardHeight + CARD_SPACING;
        }

        doc.setFontSize(8);
        doc.setTextColor("#68615E");
        doc.text(
          `Gerado em: ${new Date().toLocaleDateString("pt-BR")}`,
          pageWidth - CARD_MARGIN,
          totalHeight - 10,
          { align: "right" }
        );

        setPdfStage("Baixando o arquivo...");
        doc.save(`${filename}.pdf`);
    } catch (err) {
        console.error("Erro ao gerar PDF:", err);
        modal.showAlert({title: "Erro", message: "Não foi possível gerar o relatório.", type: "error"});
    } finally {
        setIsGeneratingPdf(false); // DESATIVA A ANIMAÇÃO
        setPdfStage("");
    }
  };

  const handleGerarPDFGeral = () => {
    const { equipes, rodadas } = ligaData;
    const calcularPontuacaoTotalTime = (timeId) =>
      rodadas
        .filter((p) => p.time_id && p.time_id.toString() === timeId.toString())
        .reduce((acc, curr) => acc + curr.pontos, 0);

    const dadosParaPdf = equipes
      .map((equipe) => {
        const timesComPontuacao = equipe.times.map((time) => ({
          ...time,
          pontuacao: calcularPontuacaoTotalTime(time.id),
        }));
        const pontuacaoTotalEquipe = timesComPontuacao.reduce(
          (acc, time) => acc + time.pontuacao,
          0
        );
        return {
          ...equipe,
          times: timesComPontuacao,
          pontuacao: pontuacaoTotalEquipe,
        };
      })
      .sort((a, b) => b.pontuacao - a.pontuacao);

    generatePdf(
      dadosParaPdf,
      "Pontuação Total Acumulada",
      "Relatorio_Geral_Acumulado"
    );
  };

  const handleGerarPDF = (rodadaNum) => {
    const { equipes, rodadas } = ligaData;
    const getPontosPorTimeNaRodada = (timeId) => {
      const ponto = rodadas.find(
        (r) =>
          r.numero === rodadaNum &&
          r.time_id &&
          r.time_id.toString() === timeId.toString()
      );
      return ponto ? ponto.pontos : 0;
    };

    const dadosParaPdf = equipes
      .map((equipe) => {
        const timesComPontuacao = equipe.times.map((time) => ({
          ...time,
          pontuacao: getPontosPorTimeNaRodada(time.id),
        }));
        const pontuacaoTotalEquipe = timesComPontuacao.reduce(
          (acc, time) => acc + time.pontuacao,
          0
        );
        return {
          ...equipe,
          times: timesComPontuacao,
          pontuacao: pontuacaoTotalEquipe,
        };
      })
      .sort((a, b) => b.pontuacao - a.pontuacao);

    generatePdf(
      dadosParaPdf,
      `Pontuação da Rodada ${rodadaNum}`,
      `Relatorio_Rodada_${rodadaNum}`
    );
  };

  const handleLogin = async (email, password) => {
    setError("");
    try {
      const data = await api.loginAdmin(email, password);
      if (data.success && data.token) {
        localStorage.setItem("adminToken", data.token);
        setIsAdmin(true);
        setView("ADMIN");
        fetchData();
      } else {
        setError(data.message || "Credenciais inválidas.");
      }
    } catch (err) {
      setError("Erro de conexão ou credenciais inválidas.");
    }
  };

  const handleAddEquipe = async (nome) => {
    try {
      await api.addEquipe(nome);
      fetchData();
      modal.showAlert({
        title: "Sucesso",
        message: "Nova equipe adicionada!",
        type: "success",
      });
    } catch (err) {
      modal.showAlert({ title: "Erro", message: err.message, type: "error" });
    }
  };

  const handleRemoveEquipe = async (id) => {
    const confirmed = await modal.showConfirm({
      title: "Remover Equipe?",
      message:
        "Esta ação removerá a equipe e todos os seus times. Deseja continuar?",
    });

    if (confirmed) {
      try {
        await api.removeEquipe(id);
        fetchData();
        modal.showAlert({
          title: "Sucesso",
          message: "Equipe removida.",
          type: "success",
        });
      } catch (err) {
        modal.showAlert({ title: "Erro", message: err.message, type: "error" });
      }
    }
  };

  const handleAddTime = async (equipeId, timeData) => {
    try {
      await api.addTime({ equipe_id: equipeId, ...timeData });
      fetchData();
      modal.showAlert({
        title: "Sucesso",
        message: `Time ${timeData.nome_time} adicionado.`,
        type: "success",
      });
    } catch (err) {
      modal.showAlert({ title: "Erro", message: err.message, type: "error" });
    }
  };

  const handleRemoveTime = async (id) => {
    const confirmed = await modal.showConfirm({
      title: "Remover Time?",
      message: "Tem certeza de que deseja remover este time?",
    });

    if (confirmed) {
      try {
        await api.removeTime(id);
        fetchData();
        modal.showAlert({
          title: "Sucesso",
          message: "Time removido.",
          type: "success",
        });
      } catch (err) {
        modal.showAlert({ title: "Erro", message: err.message, type: "error" });
      }
    }
  };

  const handleFetchScores = async (numero) => {
    const result = await api.fetchScoresForRound(numero);
    fetchData();
    return result;
  };

  const handleUpdateAllTimes = async () => {
    const result = await api.updateAllTimes();
    fetchData();
    return result;
  };

  const handleResetarPontuacoes = async () => {
    const confirmed = await modal.showConfirm({
      title: "TEM A CERTEZA?",
      message:
        "Esta ação irá apagar TODAS as pontuações de TODAS as rodadas e não pode ser desfeita.",
    });

    if (confirmed) {
      try {
        const result = await api.resetarPontuacoes();
        fetchData();
        return result;
      } catch (err) {
        throw err;
      }
    }
    return null;
  };

  const handleResetScoresForRound = async (numero) => {
    const confirmed = await modal.showConfirm({
      title: `Resetar Rodada ${numero}?`,
      message: `Tem a certeza de que deseja apagar TODAS as pontuações da rodada ${numero}? Esta ação não pode ser desfeita.`,
    });

    if (confirmed) {
      try {
        const result = await api.resetScoresForRound(numero);
        fetchData();
        return result;
      } catch (err) {
        throw err;
      }
    }
    return null;
  };

  const handleAddAdmin = async ({ email, password }) => {
    try {
      await api.addAdmin({ email, password });
      fetchData();
      modal.showAlert({
        title: "Sucesso",
        message: "Novo administrador adicionado.",
        type: "success",
      });
    } catch (err) {
      modal.showAlert({ title: "Erro", message: err.message, type: "error" });
    }
  };

  const handleDeleteAdmin = async (adminId) => {
    const confirmed = await modal.showConfirm({
      title: "Apagar Administrador?",
      message:
        "Tem a certeza de que deseja apagar este utilizador administrador?",
    });
    if (confirmed) {
      try {
        await api.deleteAdmin(adminId);
        fetchData();
        modal.showAlert({
          title: "Sucesso",
          message: "Administrador apagado.",
          type: "success",
        });
      } catch (err) {
        modal.showAlert({ title: "Erro", message: err.message, type: "error" });
      }
    }
  };

  const handleSetCaptain = async (timeId, equipeId) => {
    try {
      await api.setCaptain(timeId, equipeId);
      fetchData();
      modal.showAlert({
        title: "Sucesso",
        message: "Capitão definido com sucesso!",
        type: "success",
      });
    } catch (err) {
      modal.showAlert({ title: "Erro", message: err.message, type: "error" });
    }
  };

  const handleUploadLogo = async (equipeId, formData) => {
    try {
      await api.uploadLogo(equipeId, formData);
      fetchData();
      modal.showAlert({
        title: "Sucesso",
        message: "Logo da equipe atualizado com sucesso!",
        type: "success",
      });
    } catch (err) {
      modal.showAlert({ title: "Erro", message: err.message, type: "error" });
    }
  };

  const renderView = () => {
    if (loading) {
      return <CopaLoader label="Preparando o torneio..." />;
    }

    if (error) {
      return (
        <div className="max-w-md mx-auto mt-10 flex items-center gap-3 bg-red-500/10 border border-red-500/30 text-red-400 p-4 rounded-xl text-sm">
          <Shield size={18} className="flex-shrink-0" />
          {error}
        </div>
      );
    }

    switch (view) {
      case "LOGIN":
        return (
          <LoginPage onLogin={handleLogin} error={error} setView={setView} />
        );
      case "ADMIN":
        return (
          <AdminPanel
            equipes={ligaData.equipes}
            admins={admins}
            onAddEquipe={handleAddEquipe}
            onRemoveEquipe={handleRemoveEquipe}
            onAddTime={handleAddTime}
            onRemoveTime={handleRemoveTime}
            onFetchScores={handleFetchScores}
            onUpdateAllTimes={handleUpdateAllTimes}
            onResetScores={handleResetarPontuacoes}
            onResetScoresForRound={handleResetScoresForRound}
            onAddAdmin={handleAddAdmin}
            onDeleteAdmin={handleDeleteAdmin}
            onSetCaptain={handleSetCaptain}
            onUploadLogo={handleUploadLogo}
            onRefreshData={fetchData}
          />
        );
      default:
        return (
          <Dashboard
            ligaData={ligaData}
            parciais={parciais}
            handleGerarPDF={handleGerarPDF}
            handleGerarPDFGeral={handleGerarPDFGeral}
            isAdmin={isAdmin}
          />
        );
    }
  };

  return (
    <div className="bg-campo-950 text-tinta min-h-screen font-sans relative">
      <BackgroundCopa />
      {/* Overlay de geração de PDF */}
      {isGeneratingPdf && (
        <CopaLoader fullscreen label={pdfStage || "A gerar o seu relatório..."} />
      )}

      {/* Header sticky */}
      <header className="sticky top-0 z-40 bg-campo-950/85 backdrop-blur-md border-b border-campo-700/60">
        {/* Faixa de destaque */}
        <div className="h-1 bg-gradient-to-r from-transparent via-ouro to-transparent" />
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-3 px-4 py-2.5">
          {/* Logo: lockup em duas linhas */}
          <button
            onClick={() => setView("DASHBOARD")}
            className="flex items-center gap-2.5 group min-w-0"
          >
            <img
              src="/logo-cfc-paraiba.png"
              alt="Logo do Torneio CFC Paraíba de Equipes"
              className="w-8 h-8 flex-shrink-0"
            />
            <span className="flex flex-col items-start leading-none min-w-0">
              <span className="text-sm sm:text-lg font-black tracking-tight text-tinta group-hover:text-ouro transition-colors whitespace-nowrap">
                Torneio CFC Paraíba
              </span>
              <span className="flex items-center gap-1.5 text-[10px] sm:text-xs mt-1 whitespace-nowrap">
                <span className="font-bold uppercase tracking-[0.25em] text-ouro">
                  de Equipes
                </span>
              </span>
            </span>
          </button>

          {/* Navegação */}
          <nav className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            {view !== "DASHBOARD" && (
              <button
                onClick={() => setView("DASHBOARD")}
                className="px-3 py-1.5 rounded-lg text-sm font-medium text-tinta-soft hover:text-tinta hover:bg-campo-800 transition-colors"
              >
                Dashboard
              </button>
            )}
            {isAdmin && view !== "ADMIN" && (
              <button
                onClick={() => setView("ADMIN")}
                className="px-3 py-1.5 rounded-lg text-sm font-medium text-tinta-soft hover:text-tinta hover:bg-campo-800 transition-colors"
              >
                Admin
              </button>
            )}
            {isAdmin ? (
              <button
                onClick={handleLogout}
                className="flex items-center gap-1.5 bg-red-600/15 hover:bg-red-600 border border-red-600/40 text-red-400 hover:text-white font-semibold py-1.5 px-3 text-sm rounded-lg transition-all"
              >
                <LogOut size={15} /> Sair
              </button>
            ) : (
              view !== "LOGIN" && (
                <button
                  onClick={() => setView("LOGIN")}
                  className="flex items-center gap-1.5 bg-ouro hover:bg-ouro-light text-white font-bold py-1.5 px-3 text-sm rounded-lg transition-colors"
                >
                  <LogIn size={15} /> Admin
                </button>
              )
            )}
          </nav>
        </div>
      </header>

      <main className="relative max-w-7xl mx-auto p-4 md:p-6 lg:p-8">{renderView()}</main>

      {/* Footer temático */}
      <footer className="relative mt-8">
        <div className="h-1 bg-gradient-to-r from-transparent via-ouro to-transparent" />
        <div className="border-t border-tinta bg-tinta">
          <div className="max-w-7xl mx-auto px-4 py-5 flex flex-col sm:flex-row items-center justify-center gap-1.5 sm:gap-3 text-xs text-gray-500">
            <span className="flex items-center gap-1.5">
              <Trophy size={13} className="text-ouro" />
              <span className="font-semibold text-gray-400">Torneio CFC Paraíba de Equipes</span>
            </span>
            <span className="hidden sm:inline text-campo-600">•</span>
            <span>2026</span>
          </div>
        </div>
      </footer>
    </div>
  );
}